import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import {
  Mail,
  X,
  Users,
  Plus,
  Trash2,
  RefreshCw,
  Check,
  Shield,
  Calendar,
  AtSign,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { SyncedSharedMailbox, MailboxDelegate } from '../../types';
import { getMailboxPermissions, manageMailboxPermissions, deleteSharedMailbox } from '../../services/api';

interface SharedMailboxDetailProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  mailbox: SyncedSharedMailbox;
  onDeleted?: () => void;
}

export default function SharedMailboxDetail({
  isOpen,
  onClose,
  clientId,
  mailbox,
  onDeleted,
}: SharedMailboxDetailProps) {
  const [delegates, setDelegates] = useState<MailboxDelegate[]>([]);
  const [loading, setLoading] = useState(false);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Add delegate form
  const [showAddForm, setShowAddForm] = useState(false);
  const [newDelegateEmail, setNewDelegateEmail] = useState('');
  const [newPermissions, setNewPermissions] = useState({
    fullAccess: true,
    sendAs: false,
    sendOnBehalf: false,
  });

  async function fetchPermissions() {
    setLoading(true);
    setError(null);
    setPermissionsLoaded(true);

    try {
      // Use the mailbox id as the userId param; the mailboxEmail query param is what matters
      const response = await getMailboxPermissions(clientId, mailbox.id, mailbox.email);
      if (response.success) {
        setDelegates(response.delegates || []);
      } else {
        setError(response.error || 'Failed to load permissions');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (isOpen) {
      // Reset state on open — don't auto-fetch permissions (runbook takes 30-90s)
      setPermissionsLoaded(false);
      setDelegates([]);
      setError(null);
      setShowAddForm(false);
      setNewDelegateEmail('');
      setNewPermissions({ fullAccess: true, sendAs: false, sendOnBehalf: false });
    }
  }, [isOpen, mailbox.id]);

  async function handleAddDelegate() {
    if (!newDelegateEmail || !newDelegateEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    if (!newPermissions.fullAccess && !newPermissions.sendAs && !newPermissions.sendOnBehalf) {
      toast.error('Please select at least one permission');
      return;
    }

    setActionLoading(true);
    const toastId = toast.loading('Adding permissions... This may take up to 90 seconds.');

    try {
      const response = await manageMailboxPermissions(clientId, mailbox.id, mailbox.email, {
        delegateEmail: newDelegateEmail,
        action: 'add',
        ...newPermissions,
      });

      toast.dismiss(toastId);

      if (response.success) {
        toast.success(`Permissions added for ${newDelegateEmail}`);
        setShowAddForm(false);
        setNewDelegateEmail('');
        setNewPermissions({ fullAccess: true, sendAs: false, sendOnBehalf: false });
        fetchPermissions();
      } else {
        toast.error(response.error || response.message || 'Failed to add permissions');
      }
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err instanceof Error ? err.message : 'Failed to add permissions');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleRemoveDelegate(delegateUser: string) {
    if (!confirm(`Remove all permissions for ${delegateUser} on this mailbox?`)) return;

    setActionLoading(true);
    const toastId = toast.loading('Removing permissions... This may take up to 90 seconds.');

    try {
      const response = await manageMailboxPermissions(clientId, mailbox.id, mailbox.email, {
        delegateEmail: delegateUser,
        action: 'remove',
        fullAccess: true,
        sendAs: true,
        sendOnBehalf: true,
      });

      toast.dismiss(toastId);

      if (response.success) {
        toast.success(`Permissions removed for ${delegateUser}`);
        fetchPermissions();
      } else {
        toast.error(response.error || response.message || 'Failed to remove permissions');
      }
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err instanceof Error ? err.message : 'Failed to remove permissions');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleDeleteMailbox() {
    setDeleteLoading(true);
    const toastId = toast.loading('Deleting shared mailbox... This may take up to 90 seconds.');

    try {
      const response = await deleteSharedMailbox(clientId, mailbox.email);

      toast.dismiss(toastId);

      if (response.success) {
        toast.success(`Shared mailbox "${mailbox.displayName}" has been deleted`);
        setShowDeleteConfirm(false);
        onClose();
        if (onDeleted) {
          onDeleted();
        }
      } else {
        toast.error(response.error || 'Failed to delete shared mailbox');
      }
    } catch (err) {
      toast.dismiss(toastId);
      toast.error(err instanceof Error ? err.message : 'Failed to delete shared mailbox');
    } finally {
      setDeleteLoading(false);
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-2xl p-0 gap-0 [&>button]:hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-muted px-4 py-4 sm:px-6 border-b border-border rounded-t-lg flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center min-w-0">
              <div className="flex-shrink-0 h-10 w-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center mr-3">
                <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="min-w-0">
                <DialogTitle className="text-lg font-semibold text-foreground truncate">
                  {mailbox.displayName}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  Shared mailbox details and permissions management
                </DialogDescription>
                <p className="text-sm text-muted-foreground truncate">{mailbox.email}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground flex-shrink-0 ml-4"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 px-4 py-4 sm:px-6 space-y-6">
          {/* Mailbox Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-sm">
              <AtSign className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="text-muted-foreground">Alias:</span>
              <span className="text-foreground font-medium">{mailbox.alias}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Users className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="text-muted-foreground">Members:</span>
              <span className="text-foreground font-medium">
                {mailbox.memberCount} {mailbox.memberCount === 1 ? 'member' : 'members'}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              <span className="text-muted-foreground">Email:</span>
              <span className="text-foreground font-medium truncate">{mailbox.email}</span>
            </div>
            {mailbox.createdDate && mailbox.createdDate !== 'null' && !isNaN(new Date(mailbox.createdDate).getTime()) && (
              <div className="flex items-center gap-2 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                <span className="text-muted-foreground">Created:</span>
                <span className="text-foreground font-medium">
                  {new Date(mailbox.createdDate).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>

          {/* Members List */}
          {mailbox.members.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-foreground mb-2 flex items-center">
                <Users className="h-4 w-4 mr-1.5 text-muted-foreground" />
                Members ({mailbox.members.length})
              </h4>
              <div className="flex flex-wrap gap-2">
                {mailbox.members.map((member) => (
                  <span
                    key={member}
                    className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300"
                  >
                    {member}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Permissions Section */}
          <div className="border-t border-border pt-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-foreground flex items-center">
                <Shield className="h-4 w-4 mr-1.5 text-muted-foreground" />
                Permissions
              </h4>
              <div className="flex items-center gap-2">
                <button
                  onClick={fetchPermissions}
                  disabled={loading || actionLoading}
                  className="text-muted-foreground hover:text-foreground disabled:opacity-50"
                  title="Refresh permissions"
                >
                  <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
                <button
                  onClick={() => setShowAddForm(!showAddForm)}
                  disabled={actionLoading}
                  className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300 hover:bg-primary-200 dark:hover:bg-primary-800 disabled:opacity-50"
                >
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  Add Member
                </button>
              </div>
            </div>

            {/* Add Delegate Form */}
            {showAddForm && (
              <div className="bg-muted rounded-lg p-4 space-y-3 mb-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    User Email (UPN)
                  </label>
                  <input
                    type="email"
                    value={newDelegateEmail}
                    onChange={(e) => setNewDelegateEmail(e.target.value)}
                    placeholder="user@company.com"
                    className="w-full px-3 py-2 border border-border rounded-lg bg-card text-foreground focus:ring-2 focus:ring-primary-500 text-sm"
                    disabled={actionLoading}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddDelegate())}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Permission Type
                  </label>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newPermissions.fullAccess}
                        onChange={(e) => setNewPermissions({ ...newPermissions, fullAccess: e.target.checked })}
                        className="h-4 w-4 text-primary-600 border-border rounded focus:ring-primary-500"
                        disabled={actionLoading}
                      />
                      <span className="ml-2 text-sm text-foreground">
                        Full Access{' '}
                        <span className="text-xs text-muted-foreground">(read, manage, delete emails)</span>
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newPermissions.sendAs}
                        onChange={(e) => setNewPermissions({ ...newPermissions, sendAs: e.target.checked })}
                        className="h-4 w-4 text-primary-600 border-border rounded focus:ring-primary-500"
                        disabled={actionLoading}
                      />
                      <span className="ml-2 text-sm text-foreground">
                        Send As{' '}
                        <span className="text-xs text-muted-foreground">(send emails appearing from this mailbox)</span>
                      </span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={newPermissions.sendOnBehalf}
                        onChange={(e) => setNewPermissions({ ...newPermissions, sendOnBehalf: e.target.checked })}
                        className="h-4 w-4 text-primary-600 border-border rounded focus:ring-primary-500"
                        disabled={actionLoading}
                      />
                      <span className="ml-2 text-sm text-foreground">
                        Send on Behalf{' '}
                        <span className="text-xs text-muted-foreground">(shows "on behalf of" in recipient's email)</span>
                      </span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setShowAddForm(false)}
                    disabled={actionLoading}
                    className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAddDelegate}
                    disabled={actionLoading || !newDelegateEmail}
                    className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center"
                  >
                    {actionLoading && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
                    Add
                  </button>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-3 mb-3">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            {/* Show cached permissions if available, otherwise show members + load button */}
            {!permissionsLoaded && !loading && (
              <div>
                {mailbox.permissions && mailbox.permissions.length > 0 ? (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Permissions (from cached sync data):</p>
                    <div className="overflow-x-auto rounded-lg border border-border">
                      <table className="min-w-full divide-y divide-border">
                        <thead className="bg-muted">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                              Delegate
                            </th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground uppercase">
                              <span className="hidden sm:inline">Full Access</span>
                              <span className="sm:hidden">FA</span>
                            </th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground uppercase">
                              <span className="hidden sm:inline">Send As</span>
                              <span className="sm:hidden">SA</span>
                            </th>
                            <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground uppercase">
                              <span className="hidden sm:inline">Send on Behalf</span>
                              <span className="sm:hidden">SoB</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border bg-card">
                          {mailbox.permissions.map((perm) => (
                            <tr key={perm.delegate} className="hover:bg-accent">
                              <td className="px-3 py-2 text-sm text-foreground">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-7 w-7 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mr-2">
                                    <span className="text-xs font-medium text-foreground">
                                      {perm.delegate.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                  <span className="truncate max-w-[200px]">{perm.delegate}</span>
                                </div>
                              </td>
                              <td className="px-3 py-2 text-center">
                                {perm.fullAccess ? (
                                  <Check className="h-4 w-4 text-green-500 mx-auto" />
                                ) : (
                                  <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                                )}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {perm.sendAs ? (
                                  <Check className="h-4 w-4 text-green-500 mx-auto" />
                                ) : (
                                  <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                                )}
                              </td>
                              <td className="px-3 py-2 text-center">
                                {perm.sendOnBehalf ? (
                                  <Check className="h-4 w-4 text-green-500 mx-auto" />
                                ) : (
                                  <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <button
                      onClick={fetchPermissions}
                      className="mt-2 text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
                    >
                      Refresh live permissions (30-60s)
                    </button>
                  </div>
                ) : mailbox.members && mailbox.members.length > 0 ? (
                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Full Access members (from cached sync data):</p>
                    <div className="space-y-1 mb-3">
                      {mailbox.members.map((member, idx) => (
                        <div key={idx} className="flex items-center justify-between py-1.5 px-3 bg-muted/50 rounded text-sm">
                          <span className="font-medium text-foreground">{member}</span>
                          <span className="text-xs text-green-600 dark:text-green-400">Full Access</span>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={fetchPermissions}
                      className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400"
                    >
                      Load Send As / Send on Behalf details (30-60s)
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground mb-2">No cached permission data</p>
                    <button
                      onClick={fetchPermissions}
                      className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
                    >
                      Load Permissions
                    </button>
                    <p className="text-xs text-muted-foreground mt-1">Takes 30-60 seconds</p>
                  </div>
                )}
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="flex flex-col items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary-600 mb-2" />
                <p className="text-sm text-muted-foreground">Loading permissions via Exchange Online...</p>
                <p className="text-xs text-muted-foreground mt-1">This may take 30-60 seconds</p>
              </div>
            )}

            {/* Empty State */}
            {permissionsLoaded && !loading && !error && delegates.length === 0 && (
              <div className="text-center py-6 bg-muted/50 rounded-lg">
                <Shield className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground">
                  No delegate permissions configured
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Click "Add Member" to grant access to this mailbox
                </p>
              </div>
            )}

            {/* Delegates Table */}
            {!loading && !error && delegates.length > 0 && (
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase">
                        Delegate
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground uppercase">
                        <span className="hidden sm:inline">Full Access</span>
                        <span className="sm:hidden">FA</span>
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground uppercase">
                        <span className="hidden sm:inline">Send As</span>
                        <span className="sm:hidden">SA</span>
                      </th>
                      <th className="px-3 py-2 text-center text-xs font-medium text-muted-foreground uppercase">
                        <span className="hidden sm:inline">Send on Behalf</span>
                        <span className="sm:hidden">SoB</span>
                      </th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground uppercase">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border bg-card">
                    {delegates.map((delegate) => (
                      <tr key={delegate.user} className="hover:bg-accent">
                        <td className="px-3 py-2 text-sm text-foreground">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-7 w-7 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mr-2">
                              <span className="text-xs font-medium text-foreground">
                                {delegate.user.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="truncate max-w-[200px]">{delegate.user}</span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          {delegate.fullAccess ? (
                            <Check className="h-4 w-4 text-green-500 mx-auto" />
                          ) : (
                            <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {delegate.sendAs ? (
                            <Check className="h-4 w-4 text-green-500 mx-auto" />
                          ) : (
                            <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                          )}
                        </td>
                        <td className="px-3 py-2 text-center">
                          {delegate.sendOnBehalf ? (
                            <Check className="h-4 w-4 text-green-500 mx-auto" />
                          ) : (
                            <X className="h-4 w-4 text-muted-foreground/40 mx-auto" />
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => handleRemoveDelegate(delegate.user)}
                            disabled={actionLoading}
                            className="text-red-500 hover:text-red-700 disabled:opacity-50"
                            title={`Remove all permissions for ${delegate.user}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Danger Zone */}
          <div className="border-t border-red-200 dark:border-red-800 pt-4">
            <h4 className="text-sm font-medium text-red-600 dark:text-red-400 mb-3 flex items-center">
              <AlertTriangle className="h-4 w-4 mr-1.5" />
              Danger Zone
            </h4>

            {!showDeleteConfirm ? (
              <button
                onClick={() => setShowDeleteConfirm(true)}
                disabled={actionLoading || deleteLoading}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-600 dark:text-red-400 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
              >
                <Trash2 className="h-4 w-4 mr-1.5" />
                Delete Mailbox
              </button>
            ) : (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 space-y-3">
                <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                  Permanently delete "{mailbox.displayName}"?
                </p>
                <p className="text-sm text-red-600 dark:text-red-400">
                  All emails in this mailbox will be lost. This action cannot be undone.
                </p>
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => setShowDeleteConfirm(false)}
                    disabled={deleteLoading}
                    className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteMailbox}
                    disabled={deleteLoading}
                    className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50 flex items-center"
                  >
                    {deleteLoading && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
                    Yes, Delete Permanently
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-muted px-4 py-3 sm:px-6 border-t border-border flex justify-between items-center rounded-b-lg flex-shrink-0">
          <p className="text-xs text-muted-foreground">
            {delegates.length > 0 && !loading
              ? `${delegates.length} delegate${delegates.length !== 1 ? 's' : ''} configured`
              : '\u00A0'}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-lg hover:bg-accent"
          >
            Close
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
